/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ResearchToolCall,
  ResearchToolCall$inboundSchema,
} from "./researchtoolcall.js";

/**
 * One research-agent run over a request's server. Findings are gathered and cited, never adjudicated — and web-sourced claims may be inaccurate, incomplete, or deliberately seeded.
 */
export type ResearchReport = {
  /**
   * When the run finished.
   */
  completedAt?: Date | undefined;
  /**
   * When the run was requested.
   */
  createdAt: Date;
  /**
   * Why the run failed, when it did.
   */
  error?: string | undefined;
  /**
   * The report ID.
   */
  id: string;
  /**
   * The model that produced the report.
   */
  model?: string | undefined;
  /**
   * The prompt version the run used, so reports stay distinguishable across prompt changes.
   */
  promptVersion?: string | undefined;
  /**
   * The structured findings. Every claim carries a provenance tier and its citations.
   */
  report?: any | undefined;
  /**
   * Shape version of the report payload.
   */
  reportVersion: number;
  /**
   * Who asked for the research run.
   */
  requestedBy?: string | undefined;
  /**
   * When the run started.
   */
  startedAt?: Date | undefined;
  /**
   * The run's lifecycle state, such as running, completed, or failed.
   */
  status: string;
  /**
   * The run's per-action trace — every search and page fetch, in order. The report above is a synthesis that drops most of what was read; this is what the agent actually did.
   */
  toolCalls?: Array<ResearchToolCall> | undefined;
};

/** @internal */
export const ResearchReport$inboundSchema: z.ZodMiniType<
  ResearchReport,
  unknown
> = z.pipe(
  z.object({
    completed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    error: z.optional(z.string()),
    id: z.string(),
    model: z.optional(z.string()),
    prompt_version: z.optional(z.string()),
    report: z.optional(z.any()),
    report_version: z.int(),
    requested_by: z.optional(z.string()),
    started_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    status: z.string(),
    tool_calls: z.optional(z.array(ResearchToolCall$inboundSchema)),
  }),
  z.transform((v) => {
    return remap$(v, {
      "completed_at": "completedAt",
      "created_at": "createdAt",
      "prompt_version": "promptVersion",
      "report_version": "reportVersion",
      "requested_by": "requestedBy",
      "started_at": "startedAt",
      "tool_calls": "toolCalls",
    });
  }),
);

export function researchReportFromJSON(
  jsonString: string,
): SafeParseResult<ResearchReport, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ResearchReport$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ResearchReport' from JSON`,
  );
}
